import React, { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import Layout from "../components/Layout";
import { UserAvatar } from "../components/ui/user-avatar";
import { Button } from "../components/ui/button";
import { ChipGroup } from "../components/ui/chip-group";
import { FilterDropdown } from "../components/ui/filter-dropdown";
import { DataTable, DataTableColumn } from "../components/ui/data-table";
import ActivityLogModal from "../components/ActivityLogModal";
import { ActivityLog } from "../api/api";
import { useAudit, useUsers, usePartners } from "../hooks/queries";
import { cn } from "@/lib/utils";

const TONES = ["blue", "green", "amber", "stone"] as const;
const LIMIT = 25;

const ACTION_CLASSES: Record<string, string> = {
  create:    "bg-status-ok-bg text-status-ok-fg",
  confirmed: "bg-status-ok-bg text-status-ok-fg",
  delete:    "bg-status-alert-bg text-status-alert-fg",
  status_change: "bg-status-warn-bg text-status-warn-fg",
};

const ACTION_LABELS: Record<string, string> = {
  create: "aangemaakt",
  update: "bijgewerkt",
  delete: "verwijderd",
  confirmed: "gecontroleerd",
  status_change: "status gewijzigd",
  login: "ingelogd",
};

const MODEL_LABELS: Record<string, string> = {
  job_openings: "Vacature",
  users: "Gebruiker",
  partners: "Partner",
  branches: "Branch",
};

function modelLabel(model: string): string {
  return MODEL_LABELS[model] ?? model.replace(/_/g, " ");
}

// Category chips. The audit list endpoint can't filter on action/model, so
// these (like the Actie/Object dropdowns) filter client-side on the current
// page only. Mapping onto activity_logs:
// - Vacaturecheck: action "confirmed" (a vacature confirmed as still current)
// - Vacatures:     other job_openings changes (create/update/delete/status_change)
// - Logins:        action "login" (model_name is the login provider)
// - Beheer:        everything else (users, partners, branches, alert rules, …)
type Category = "alles" | "vacaturecheck" | "vacatures" | "beheer" | "logins";

const CATEGORY_OPTIONS: { value: Category; label: string }[] = [
  { value: "alles", label: "Alles" },
  { value: "vacaturecheck", label: "Vacaturecheck" },
  { value: "vacatures", label: "Vacatures" },
  { value: "beheer", label: "Beheer" },
  { value: "logins", label: "Logins" },
];

function categoryOf(r: ActivityLog): Exclude<Category, "alles"> {
  if (r.action === "login") return "logins";
  if (r.action === "confirmed") return "vacaturecheck";
  if (r.model_name === "job_openings") return "vacatures";
  return "beheer";
}

const ACTION_OPTIONS = [
  { value: "alle", label: "Alle acties" },
  { value: "create", label: "Aangemaakt" },
  { value: "update", label: "Bijgewerkt" },
  { value: "delete", label: "Verwijderd" },
];

export default function AuditTrail() {
  const [page, setPage] = useState(1);

  const [category, setCategory]         = useState<Category>("alles");
  const [filterActie, setFilterActie]   = useState("alle");
  const [filterModel, setFilterModel]   = useState("alle");
  const [filterUser, setFilterUser]     = useState("alle");
  const [filterPartner, setFilterPartner] = useState("alle");
  const [selectedLog, setSelectedLog]   = useState<ActivityLog | null>(null);

  const { data, isLoading: loading } = useAudit(page, LIMIT, {
    actorEmail:  filterUser !== "alle" && filterUser !== "waves" ? filterUser : undefined,
    actorDomain: filterUser === "waves" ? "wavesnetwork.com" : undefined,
    partnerId:   filterPartner !== "alle" ? Number(filterPartner) : undefined,
  });
  const { data: users = [] } = useUsers();
  const { data: partnerList = [] } = usePartners();

  useEffect(() => { setPage(1); }, [filterUser, filterPartner]);

  const allRows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const uniqueModels = useMemo(() =>
    [...new Set(allRows.map(r => r.model_name))].sort(),
    [allRows]
  );

  const rows = useMemo(() => {
    let result = allRows;
    if (category !== "alles") result = result.filter(r => categoryOf(r) === category);
    if (filterActie !== "alle") result = result.filter(r => r.action === filterActie);
    if (filterModel !== "alle") result = result.filter(r => r.model_name === filterModel);
    return result;
  }, [allRows, category, filterActie, filterModel]);

  const toneByRow = useMemo(() => new Map(allRows.map((r, i) => [r.id, TONES[i % 4]])), [allRows]);

  const userOptions = [
    { value: "alle", label: "Alle gebruikers" },
    { value: "waves", label: "Waves gebruikers" },
    ...users.map(u => ({ value: u.email, label: u.name })),
  ];
  const partnerOptions = [
    { value: "alle", label: "Alle partners" },
    ...partnerList.map(p => ({ value: String(p.id), label: p.name })),
  ];
  const modelOptions = [
    { value: "alle", label: "Alle objecten" },
    ...uniqueModels.map(m => ({ value: m, label: modelLabel(m) })),
  ];

  const hasFilters = category !== "alles" || filterActie !== "alle" || filterModel !== "alle" || filterUser !== "alle" || filterPartner !== "alle";

  function resetFilters() {
    setCategory("alles"); setFilterActie("alle"); setFilterModel("alle"); setFilterUser("alle"); setFilterPartner("alle"); setPage(1);
  }

  const columns: DataTableColumn<ActivityLog>[] = [
    {
      key: "created_at",
      header: "Tijdstip",
      width: "150px",
      sortValue: r => r.created_at,
      cell: r => (
        <span className="whitespace-nowrap font-mono text-[13px] tabular-nums text-waves-muted">
          {new Date(r.created_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
        </span>
      ),
    },
    {
      key: "actor_email",
      header: "Gebruiker",
      width: "minmax(200px,1.4fr)",
      sortValue: r => r.actor_email,
      cell: r => (
        <div className="flex min-w-0 items-center gap-2.5">
          <UserAvatar name={r.actor_email} size={26} tone={toneByRow.get(r.id) ?? "blue"} />
          <div className="min-w-0">
            <div className="truncate font-bold">{r.actor_email.split("@")[0]}</div>
            <div className="truncate text-[13px] text-waves-muted">{r.actor_email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "action",
      header: "Actie",
      width: "150px",
      sortValue: r => r.action,
      cell: r => (
        <span className={cn(
          "inline-flex rounded-pill px-2.5 py-1 text-[13px] font-bold leading-none",
          ACTION_CLASSES[r.action] ?? "bg-status-neutral-bg text-status-neutral-fg",
        )}>
          {ACTION_LABELS[r.action] ?? r.action}
        </span>
      ),
    },
    {
      key: "model_name",
      header: "Type / object",
      width: "minmax(160px,1fr)",
      sortValue: r => r.model_name,
      cell: r => (
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-waves-body">{r.action === "login" ? `via ${r.model_name}` : modelLabel(r.model_name)}</span>
          {r.record_id > 0 && <span className="font-mono text-[13px] text-waves-muted">#{r.record_id}</span>}
        </div>
      ),
    },
  ];

  return (
    <Layout
      title="Audit trail"
      subtitle={<>Volledige wijzigingsgeschiedenis · <span className="font-mono">{total.toLocaleString("nl-NL")}</span> events</>}
    >
      <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
        <ChipGroup aria-label="Categorie" value={category} onChange={setCategory} options={CATEGORY_OPTIONS} />

        <div className="flex flex-wrap items-center gap-3">
          <FilterDropdown aria-label="Actie" value={filterActie} onChange={v => { setFilterActie(v); setPage(1); }} options={ACTION_OPTIONS} />
          <FilterDropdown aria-label="Object" value={filterModel} onChange={v => { setFilterModel(v); setPage(1); }} options={modelOptions} />
          <FilterDropdown aria-label="Gebruiker" value={filterUser} onChange={setFilterUser} options={userOptions} />
          <FilterDropdown aria-label="Partner" value={filterPartner} onChange={setFilterPartner} options={partnerOptions} />
          {hasFilters && (
            <button onClick={resetFilters} className="flex items-center gap-1 px-1 text-[13px] font-bold text-waves-muted hover:text-waves-ink">
              <X size={13} /> Wis filters
            </button>
          )}
          <div className="ml-auto font-mono text-[13px] text-waves-muted">{rows.length} rijen</div>
        </div>

        <DataTable
          columns={columns}
          rows={rows}
          rowKey={r => r.id}
          onRowClick={setSelectedLog}
          minWidth={760}
          empty={loading ? "Laden…" : "Geen events gevonden."}
        />

        <div className="flex items-center justify-between">
          <span className="font-mono text-[13px] text-waves-muted">
            {total === 0 ? 0 : ((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} van {total.toLocaleString("nl-NL")}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft /> Vorige
            </Button>
            <span className="px-2 font-mono text-[13px] text-waves-body">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Volgende <ChevronRight />
            </Button>
          </div>
        </div>
      </div>

      <ActivityLogModal
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
        showContext
      />
    </Layout>
  );
}
